import React, { Component, ReactNode, ErrorInfo } from 'react'
import { Box, Typography, Button, Card, CardContent } from '@mui/material'
import { Refresh as RefreshIcon, Home as HomeIcon } from '@mui/icons-material'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  retryCount: number
}

class ImprovedErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null

  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      retryCount: 0,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)

    this.setState({
      error,
    })

    // Log error for monitoring
    if (process.env.NODE_ENV === 'production') {
      // Send to error tracking service
      console.error('Production error:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      })
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId)
    }
  }

  handleRetry = () => {
    const { retryCount } = this.state

    if (retryCount < 3) {
      this.setState({
        hasError: false,
        error: undefined,
        retryCount: retryCount + 1,
      })
    } else {
      // After 3 retries, reload the page
      window.location.reload()
    }
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  handleReload = () => {
    window.location.reload()
  }

  isChunkLoadError = (error?: Error): boolean => {
    if (!error) return false

    const message = error.message.toLowerCase()
    return (
      message.includes('loading chunk') ||
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('mime type') ||
      message.includes('text/html')
    )
  }

  render() {
    const { hasError, error, retryCount } = this.state
    const { children, fallback } = this.props

    if (hasError) {
      const isChunkError = this.isChunkLoadError(error)

      if (fallback) {
        return fallback
      }

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          p={3}
          bgcolor="#f5f5f5"
        >
          <Card sx={{ maxWidth: 500, width: '100%' }}>
            <CardContent sx={{ textAlign: 'center', p: 4 }}>
              <Typography
                variant="h5"
                component="h1"
                gutterBottom
                color="error"
              >
                {isChunkError ? 'Loading Issue' : 'Something went wrong'}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {isChunkError
                  ? "We're having trouble loading part of the application. This might be due to a network issue or browser cache."
                  : 'An unexpected error occurred. Please try refreshing the page.'}
              </Typography>

              {process.env.NODE_ENV === 'development' && error && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f0f0f0', borderRadius: 1 }}>
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}
                  >
                    {error.message}
                  </Typography>
                </Box>
              )}

              <Box display="flex" flexDirection="column" gap={2} width="100%">
                {retryCount < 3 ? (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleRetry}
                    fullWidth
                  >
                    Try Again {retryCount > 0 && `(${retryCount}/3)`}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleReload}
                    fullWidth
                  >
                    Reload Page
                  </Button>
                )}

                <Button
                  variant="outlined"
                  startIcon={<HomeIcon />}
                  onClick={this.handleGoHome}
                  fullWidth
                >
                  Go to Home
                </Button>
              </Box>

              {isChunkError && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 2, display: 'block' }}
                >
                  If this problem persists, try clearing your browser cache or
                  refreshing the page.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )
    }

    return children
  }
}

export default ImprovedErrorBoundary
